import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { ProgressRequest } from 'libs/end-to-end/src/custom-viz/ProgressTypes';
import { HeatmapAnalysisRequest } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisTypes';
import { getMetricByNameFromObservation } from 'libs/end-to-end/src/me';
import { getDimensionByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getPrivilegedLevelForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { SuccessPhenotypeV2Request } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeTypes';
import { PipelineAnalysisRequest } from 'libs/end-to-end/src/custom-viz/PipelineAnalysisTypes';
import { BinScatterMultiSelectRequest } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectTypes';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { convertFilterDefinitionForExtrasToFilterStoreValue } from 'libs/end-to-end/src/convert-filter-definition-for-extras-to-filter-store-value';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';

export const getProgressFilterValues = ({
  myObservation,
  customVizRequest,
}: {
  myObservation: MyObservation;
  customVizRequest: ProgressRequest;
}) => {
  const { focusArea, dimension, metric } = customVizRequest.inputs.extras;

  const metricObject = getMetricByNameFromObservation({
    focusArea,
    metricName: metric,
    myObservation,
  });

  const dimensionObject = getDimensionByColumnHeaderValueFromObservation(
    myObservation,
    dimension,
  );

  return {
    metric: metricObject,
    dimension: dimensionObject,
  };
};

export const getHeatmapFilterValues = ({
  myObservation,
  customVizRequest,
}: {
  myObservation: MyObservation;
  customVizRequest: HeatmapAnalysisRequest;
}) => {
  const { dimension, treatmentDimensionLevel } = customVizRequest.inputs.extras;

  const level = getLevelByColumnValueFromObservation(
    myObservation,
    dimension,
    treatmentDimensionLevel || '',
  );

  // Get the privileged level display
  const privilegedLevel = getPrivilegedLevelForDimensionFromObservation(
    myObservation,
    dimension,
  );

  return {
    level,
    privilegedLevel,
  };
};

export const getSuccessFactorsFilterValues = ({
  myObservation,
  customVizRequest,
}: {
  myObservation: MyObservation;
  customVizRequest: SuccessPhenotypeV2Request;
}) => {
  const { metric } = customVizRequest.inputs.extras;

  // Get the metric display name
  const metricObject = getMetricByNameFromObservation({
    focusArea: 'Performance',
    metricName: metric,
    myObservation,
  });

  return { metric: metricObject };
};

export const getPipelineAnalysisFilterValues = ({
  myObservation,
  customVizRequest,
}: {
  myObservation: MyObservation;
  customVizRequest: PipelineAnalysisRequest;
}) => {
  const { dimension } = customVizRequest.inputs.extras;
  const dimensionObject = getDimensionByColumnHeaderValueFromObservation(
    myObservation,
    dimension,
  );

  return { dimension: dimensionObject };
};

export const getBinScatterMultiSelectFilterValues = ({
  myObservation,
  customVizRequest,
}: {
  myObservation: MyObservation;
  customVizRequest: BinScatterMultiSelectRequest;
}) => {
  const { dimension, focusArea, metric } = customVizRequest.inputs.extras;
  // Get the metric display name
  const metricObject = getMetricByNameFromObservation({
    focusArea,
    metricName: metric,
    myObservation,
  });

  const dimensionObject = getDimensionByColumnHeaderValueFromObservation(
    myObservation,
    dimension,
  );

  return {
    metric: metricObject,
    dimension: dimensionObject,
  };
};

export const getBiasAnalysisFilterValues = ({
  myObservation,
  customVizRequest,
  metric,
}: {
  myObservation: MyObservation;
  customVizRequest: BiasAnalysisRequest;
  metric: string;
}) => {
  const { dimension, treatmentDimensionLevel, focusArea } =
    customVizRequest.inputs.extras;

  const level = getLevelByColumnValueFromObservation(
    myObservation,
    dimension,
    treatmentDimensionLevel || '',
  );

  const privilegedLevel = getPrivilegedLevelForDimensionFromObservation(
    myObservation,
    dimension,
  );

  const metricObject = getMetricByNameFromObservation({
    focusArea,
    metricName: metric,
    myObservation,
  });

  return { level, privilegedLevel, metricObject };
};

export const getSecondaryFilterValues = ({
  filters,
  filterDefinitions,
}: {
  filters: FilterDefinitionForExtras[];
  filterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
}): {
  filterName: string;
  filterValues: FilterStoreValue | null;
}[] => {
  const filterValues = filters.map((filter) => {
    const filterName = filter.columnHeaderValue;

    const filterValues = convertFilterDefinitionForExtrasToFilterStoreValue({
      filterDefinitionForExtras: filter,
      filterSetDefinitions: filterDefinitions,
    });

    return {
      filterName,
      filterValues,
    };
  });

  return filterValues;
};
